import React, { useMemo } from 'react'

import useApiService from 'services/api'
import { InitialDoorData } from 'components/Samples/SampleRequestFormModal'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyContentSections,
  CompanyPageLayout,
} from 'components/NewCompanyPage'
import { DoorsModal } from 'components/NewCompanyPage/Modals/DoorsModal'
import { Modal } from 'components/UI/Modal'
import { MenuTable } from 'components/Tables/MenuTable'
import { DoorsTable } from 'components/Tables/DoorsTable/DoorsTable'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import {
  DescriptionAndContactInfo,
  Divider,
  MetricCard,
  StatsCard,
  AdditionalCharacteristics,
  Rating,
  DemographicsSection,
  HoursOfOperation,
  ForecastingSection,
} from 'components/NewCompanyPage/Sidebar/shared'
import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { formatInteger } from 'utils/formatting'

import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import useDocumentTitle from 'components/useDocumentTitle'
import { MapCard } from 'components/NewCompanyPage/Sidebar/shared/MapCard'

export function NewRestaurantLocationPage({
  restaurantID,
}: {
  restaurantID: string
}) {
  const api = useApiService()
  const { data: doorProxyData, isLoading } = api.useGetPlaceV2(restaurantID)
  const { preferences } = usePreferences()

  const chainProxyId = doorProxyData?.chain_proxy?.id
  const { data: companyData } = api.useGetCompany(chainProxyId ?? 0)

  // Set document title
  useDocumentTitle(
    `${doorProxyData?.restaurant?.restaurant_name || 'Not Found'} (Door) - First Bite`
  )

  // Modal state management
  const [showMenuModal, setShowMenuModal] = React.useState(false)
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  // Pre-compute door data for sample requests to avoid redundant API calls
  // The data is already loaded via useGetPlaceV2
  const initialDoorData: InitialDoorData | undefined = useMemo(() => {
    if (!doorProxyData?.restaurant || !companyData) return undefined

    const restaurant = doorProxyData.restaurant
    const streetAddress = restaurant.address?.split(',')[0]?.trim() || ''

    return {
      name: restaurant.restaurant_name || '',
      address: streetAddress,
      city: restaurant.city || '',
      state: restaurant.state || '',
      zipcode: restaurant.address_info?.zip || '',
      companyName: companyData.name || '',
    }
  }, [doorProxyData?.restaurant, companyData])

  const entityBreadcrumbs = useMemo(
    () => [
      ...(companyData?.name
        ? [
            {
              title: 'Company',
              label: companyData.name,
              path: `/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Chain]}${doorProxyData?.chain_proxy?.chain?.id}`,
            },
          ]
        : []),
      ...(doorProxyData?.restaurant?.restaurant_name
        ? [
            {
              title: 'Door',
              label: doorProxyData.restaurant.restaurant_name,
            },
          ]
        : []),
    ],
    [
      companyData?.name,
      preferences?.company_type_prefix_map,
      doorProxyData?.chain_proxy?.chain?.id,
      doorProxyData?.restaurant?.restaurant_name,
    ]
  )

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  // Helper function to render predicted traffic badges
  const renderPredictedTraffic = (group: string) => {
    const badges = group.split(',').map((traffic) => traffic.trim())
    return (
      <div className="flex gap-1">
        {badges.map((badge, index) => (
          <PredictedTrafficBadge key={index} value={badge} size="small" />
        ))}
      </div>
    )
  }

  // Helper function to get Instagram handle from URL
  const getInstagramHandle = (url: string) => {
    try {
      const match = url.match(/instagram\.com\/([^/?]+)/)
      return match ? `@${match[1]}` : url
    } catch {
      return url
    }
  }

  // Helper function to render Instagram ranking badge
  const renderInstagramRanking = (textPercentile: string | null) => {
    return <PredictedTrafficBadge value={textPercentile} size="small" />
  }

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      description={doorProxyData?.restaurant?.description}
      phone={doorProxyData?.restaurant?.phone}
      address={doorProxyData?.restaurant?.address}
      googlePlaceUrl={doorProxyData?.restaurant?.google_place_url}
    />
  )

  // Metrics - Chain Size and Menu Size
  summaryTabContent.push(
    <div key="metrics" className="grid grid-cols-2 gap-3">
      <MetricCard
        key="chain-size"
        value={formatInteger(doorProxyData?.chain_proxy?.chain?.count || 0)}
        label="Chain Size"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
      <MetricCard
        key="menu-size"
        value={formatInteger(doorProxyData?.restaurant?.menu_size || 0)}
        label="Menu Size"
        actionText="View Menu"
        onActionClick={() => setShowMenuModal(true)}
      />
    </div>
  )

  // Build stats items for the summary tab
  const stats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cuisine',
      value: doorProxyData?.restaurant?.cuisine_50 || '-',
    },
    {
      label: 'Predicted Traffic',
      value: doorProxyData?.restaurant?.velocity_group
        ? renderPredictedTraffic(doorProxyData.restaurant.velocity_group)
        : '-',
    },
    {
      label: 'Expense Category',
      value: doorProxyData?.restaurant?.expense_category ? (
        <span className="font-medium text-green-700">
          {doorProxyData.restaurant.expense_category}
        </span>
      ) : (
        '-'
      ),
    },
    {
      label: 'Average Rating',
      value: doorProxyData?.restaurant?.avg_rating ? (
        <Rating value={doorProxyData.restaurant.avg_rating} />
      ) : (
        '-'
      ),
    },
    {
      label: 'Total Reviews',
      value: doorProxyData?.restaurant?.total_reviews_count
        ? formatInteger(doorProxyData.restaurant.total_reviews_count)
        : '0',
    },
  ]

  // Stats Section
  summaryTabContent.push(<StatsCard key="stats" stats={stats} />)
  summaryTabContent.push(<Divider key="divider-1" />)

  // Build Instagram stats items - always show
  const instagramStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Instagram Handle',
      value: doorProxyData?.chain_proxy?.chain?.instagram?.url ? (
        <AFbLink
          className="!text-sm !no-underline !text-brand-bright hover:!text-brand-active transition-colors"
          href={doorProxyData.chain_proxy.chain.instagram.url}
          target="_blank"
        >
          {getInstagramHandle(doorProxyData.chain_proxy.chain.instagram.url)}
        </AFbLink>
      ) : (
        '-'
      ),
    },
    {
      label: 'Instagram Posts',
      value: doorProxyData?.chain_proxy?.chain?.instagram?.posts
        ? formatInteger(doorProxyData.chain_proxy.chain.instagram.posts)
        : '0',
    },
    {
      label: 'Instagram Ranking',
      value: doorProxyData?.chain_proxy?.chain?.instagram?.text_percentile
        ? renderInstagramRanking(
            doorProxyData.chain_proxy.chain.instagram.text_percentile
          )
        : '-',
    },
    {
      label: 'Instagram Followers',
      value: doorProxyData?.chain_proxy?.chain?.instagram?.followers
        ? formatInteger(doorProxyData.chain_proxy.chain.instagram.followers)
        : '0',
    },
  ]

  summaryTabContent.push(
    <StatsCard key="instagram-stats" stats={instagramStats} />
  )
  summaryTabContent.push(<Divider key="divider-2" />)

  // Forecasting Section
  summaryTabContent.push(
    <ForecastingSection
      key="forecasting"
      chainId={doorProxyData?.chain_proxy?.id}
      doorId={doorProxyData?.id}
    />
  )

  // Build characteristics array from comma-separated strings
  const characteristics = [
    ...(doorProxyData?.restaurant?.service
      ? doorProxyData.restaurant.service.split(',').map((s) => s.trim())
      : []),
    ...(doorProxyData?.restaurant?.atmosphere
      ? doorProxyData.restaurant.atmosphere.split(',').map((s) => s.trim())
      : []),
    ...(doorProxyData?.restaurant?.offerings
      ? doorProxyData.restaurant.offerings.split(',').map((s) => s.trim())
      : []),
    ...(doorProxyData?.restaurant?.open_hours
      ? Object.entries(doorProxyData.restaurant.open_hours).map(
          ([day, hours]) => `${day}: ${hours}`
        )
      : []),
  ].filter(Boolean)

  // Additional Characteristics
  if (characteristics.length > 0) {
    summaryTabContent.push(
      <AdditionalCharacteristics
        key="characteristics"
        characteristics={characteristics}
      />
    )
  }

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = [
    <MapCard
      key="map-card"
      latitude={doorProxyData?.restaurant.latitude}
      longitude={doorProxyData?.restaurant.longitude}
    />,
  ]

  // Hours of Operation
  if (doorProxyData?.restaurant?.open_hours) {
    locationDetailsTabContent.push(
      <HoursOfOperation
        key="hours-of-operation"
        openHours={doorProxyData.restaurant.open_hours}
      />
    )
  }

  // Demographics Section
  if (doorProxyData?.restaurant?.address_info) {
    locationDetailsTabContent.push(
      <Divider key="divider-demographics" />,
      <DemographicsSection
        key="demographics"
        addressInfo={doorProxyData.restaurant.address_info}
      />
    )
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData?.id as number}
        doorId={doorProxyData?.restaurant?.firstbite_id}
        address={doorProxyData?.restaurant?.address}
        phone={doorProxyData?.restaurant?.phone}
        breadcrumbRoot={
          companyData?.sub_category?.name || 'Commercial - Restaurant Chain'
        }
        breadcrumbs={[
          {
            title: 'Chain',
            breadcrumb: companyData?.name || '',
            url: `/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Chain]}${doorProxyData?.chain_proxy?.chain?.id}`,
          },
          {
            title: 'Door',
            breadcrumb: doorProxyData?.restaurant?.restaurant_name || '',
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'commercial',
          subCategorySlug: 'restaurants-bars',
        }}
        sidebar={{
          header: {
            name: doorProxyData?.restaurant?.restaurant_name || '',
            domain: companyData?.domain || '',
            logoUrl: doorProxyData?.restaurant?.logo_url,
            closedStatus: doorProxyData?.restaurant?.closed_status as
              | 'Open'
              | 'Temporarily closed'
              | 'Permanently closed'
              | undefined,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData?.id as number}
          doorProxyId={doorProxyData?.id}
          companyName={doorProxyData?.restaurant?.restaurant_name || ''}
          websiteAddress={companyData?.domain || ''}
          companyTypeSlug="restaurants-bars"
          tableTypeSlug="restaurants-bars-doors"
          isUserCreated={false}
          isDoorPage={true}
          initialDoorData={initialDoorData}
        />
      </CompanyPageLayout>

      {/* Menu Modal */}
      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {doorProxyData?.restaurant?.restaurant_name} Menu
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MenuTable chainId={doorProxyData?.chain_proxy?.chain?.id} />
        </Modal.Body>
      </Modal>

      {/* Doors Modal */}
      {companyData && doorProxyData?.chain_proxy?.chain && (
        <DoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          chainData={{
            id: doorProxyData.chain_proxy.chain.id,
            name: companyData.name || '',
            domain: companyData.domain || '',
            contact_company_id: companyData.id as number,
            count: doorProxyData.chain_proxy.chain.count || 0,
          }}
          chainID={doorProxyData.chain_proxy.chain.id}
          forChainProxyId={companyData.id as number}
          chainName={companyData.name || ''}
          subCategory={companyData.sub_category}
          matchedTableComponent={(props) => (
            <FBPageTableContainer
              style={{
                height: '540px',
                width: '100%',
              }}
            >
              <DoorsTable
                tableKeyForColumnOrdering="restaurant-chain-doors-table"
                hideTitle={true}
                disableUrlPagination
                chainFilter={doorProxyData.chain_proxy.chain.id}
                {...props}
              />
            </FBPageTableContainer>
          )}
          unmatchedTableKeyPrefix="unmatched-restaurant-doors-table"
        />
      )}
    </>
  )
}
